/**
 * useShelfMapEligibility – imperative eligibility check hook for shelf-map launchers.
 *
 * Issue: #227 – CORE – Warehouse Shelf Map – Map Eligibility Guardrails & No-Location Fallback
 *
 * Use this hook in Front Window and Catalog Management launchers to:
 *   1. Check eligibility before opening the map viewer.
 *   2. Surface user-friendly ineligibility messages.
 *   3. Track loading state during the async check.
 *
 * The hook does NOT auto-run on mount – call checkEligibility() explicitly
 * (e.g. on button click). This avoids unnecessary network requests.
 */

import { useState, useCallback } from 'react';
import { shelfMapService } from '@/services/shelfMapService';
import type { ShelfMapEligibilityResult, ShelfMapEligibilityInput } from '@/services/shelfMapService';

export interface UseShelfMapEligibilityReturn {
  /** True while the eligibility check is running. */
  isChecking: boolean;
  /** The result from the last eligibility check. Null before first check. */
  result: ShelfMapEligibilityResult | null;
  /** Run the eligibility check. Resolves to the result. */
  checkEligibility: (input: ShelfMapEligibilityInput) => Promise<ShelfMapEligibilityResult>;
  /** Reset result to null (e.g. when input changes). */
  reset: () => void;
}

/**
 * Hook for imperative eligibility checking before map launch.
 *
 * @example
 * ```tsx
 * const { isChecking, result, checkEligibility } = useShelfMapEligibility();
 *
 * const handleOpen = async () => {
 *   const res = await checkEligibility({ warehouse_entity_id: wid, primary_location_id: lid });
 *   if (res.eligible) setOpen(true);
 * };
 * ```
 */
export const useShelfMapEligibility = (): UseShelfMapEligibilityReturn => {
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<ShelfMapEligibilityResult | null>(null);

  const checkEligibility = useCallback(
    async (input: ShelfMapEligibilityInput): Promise<ShelfMapEligibilityResult> => {
      setIsChecking(true);
      try {
        const res = await shelfMapService.checkEligibility(input);
        setResult(res);
        return res;
      } catch {
        // Network/auth errors: treat as ineligible so the UI doesn't crash
        const fallback: ShelfMapEligibilityResult = {
          eligible: false,
          reason: 'no-map-config',
          message: 'Unable to verify map eligibility. Please try again.',
        };
        setResult(fallback);
        return fallback;
      } finally {
        setIsChecking(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setResult(null);
  }, []);

  return { isChecking, result, checkEligibility, reset };
};
